import { useState, useEffect, useCallback } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { TruncatedText } from '@/components/common/TruncatedText'
import { EditViewPanel } from './EditViewPanel'
import { AgentProxyService } from '@/services/agentProxyService'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'

interface ViewColumn {
  name: string
  type: string
  nullable: boolean
  defaultValue?: string
}

interface ViewDetails {
  name: string
  schema?: string
  isUpdatable?: boolean
  columns?: ViewColumn[]
  comment?: string
  definition?: string
}

interface ViewDetailsViewProps {
  connection: DatabaseConnection
  database: string
  viewName: string
  schema?: string
  openEditPanel?: boolean
  onEditPanelClose?: () => void
  onViewData?: () => void
}

export function ViewDetailsView({ connection, database, viewName, schema, openEditPanel = false, onEditPanelClose, onViewData }: ViewDetailsViewProps) {
  const [view, setView] = useState<ViewDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { showToast } = useToast()

  const fetchView = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await AgentProxyService.getViewDefinition(
        connection.id,
        database,
        viewName,
        schema
      )
      
      // Handle the response which contains view definition and metadata
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        const viewData: Record<string, unknown> = response !== null ? (response as unknown as Record<string, unknown>) : {}
        const metadataUnknown = viewData.metadata
        const metadata: Record<string, unknown> | undefined = (metadataUnknown && typeof metadataUnknown === 'object' && !Array.isArray(metadataUnknown)) ? (metadataUnknown as unknown as Record<string, unknown>) : undefined

        const getProp = (obj: unknown, key: string): unknown => {
          if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
            const rec = obj as unknown as Record<string, unknown>
            return key in rec ? rec[key] : undefined
          }
          return undefined
        }

        const schemaFromMeta = typeof metadata?.schema_name === 'string' ? metadata.schema_name : undefined
        const schemaFromView = (() => {
          const v = getProp(viewData, 'schema_name')
          return typeof v === 'string' ? v : undefined
        })()
        const schemaFinal = schemaFromMeta ?? schemaFromView ?? (schema || '')

        const isUpdatableFinal = (() => {
          if (typeof metadata?.is_updatable === 'boolean') return metadata.is_updatable
          const v = getProp(viewData, 'is_updatable')
          return typeof v === 'boolean' ? v : false
        })()

        const commentFinal = (() => {
          if (typeof metadata?.comment === 'string') return metadata.comment
          const v = getProp(viewData, 'comment')
          return typeof v === 'string' ? v : ''
        })()

        const extractColumns = (source: unknown): ViewColumn[] => {
          const maybeCols = getProp(source, 'columns')
          if (Array.isArray(maybeCols)) {
            return maybeCols.map((c) => {
              const nameUnknown = getProp(c, 'name')
              const typeUnknown = getProp(c, 'type')
              const nullableUnknown = getProp(c, 'nullable')
              const defaultUnknown = getProp(c, 'defaultValue')
              return {
                name: typeof nameUnknown === 'string' ? nameUnknown : '',
                type: typeof typeUnknown === 'string' ? typeUnknown : '',
                nullable: Boolean(nullableUnknown),
                defaultValue: typeof defaultUnknown === 'string' ? defaultUnknown : undefined,
              }
            })
          }
          return []
        }

        setView({
          name: viewName,
          definition: typeof viewData.definition === 'string' ? viewData.definition : '',
          schema: schemaFinal,
          isUpdatable: isUpdatableFinal,
          comment: commentFinal,
          columns: (() => {
            const colsFromMeta = extractColumns(metadata)
            if (colsFromMeta.length > 0) return colsFromMeta
            return extractColumns(viewData)
          })()
        })
      } else {
        console.error('Failed to fetch view details: Invalid response format')
        showToast('error', 'Failed to load view details')
        setView(null)
      }
    } catch (error) {
      console.error('Failed to fetch view details:', error)
      showToast('error', 'Failed to load view details')
      setView(null)
    } finally {
      setIsLoading(false)
    }
  }, [connection.id, database, viewName, schema, showToast])

  useEffect(() => {
    void fetchView()
  }, [fetchView])

  useEffect(() => {
    if (openEditPanel && view) {
      setIsEditPanelOpen(true)
    }
  }, [openEditPanel, view])

  const handleEditSuccess = () => {
    void fetchView()
  }

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true)
      await AgentProxyService.dropView(
        connection.id,
        database,
        viewName,
        schema
      )
      
      showToast('success', `View "${viewName}" deleted successfully`)
      // Navigate back or refresh parent
    } catch (error: unknown) {
      console.error('Failed to drop view:', error)
      const message = error instanceof Error ? error.message : 'Failed to drop view'
      showToast('error', message)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!view) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            View Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The view "{viewName}" could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="text-3xl mr-4">👁️</div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {view.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Database View • {schema || (connection.type === 'mysql' ? database : 'public')} schema
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {onViewData && (
                <button type="button"
                  onClick={onViewData}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Data
                </button>
              )}
              
              <button type="button"
                onClick={() => { setIsEditPanelOpen(true) }}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit View
              </button>
              
              <button type="button"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Dropping...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Drop View
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* View Properties */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            View Properties
          </h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Name
              </label>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                <TruncatedText 
                  text={view.name || viewName} 
                  maxLength={15}
                />
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Schema
              </label>
              <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                <TruncatedText 
                  text={view.schema || schema || 'N/A'} 
                  maxLength={15}
                />
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Updatable
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  view.isUpdatable
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {view.isUpdatable ? 'Yes' : 'No'}
                </span>
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Columns
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {view.columns?.length || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Comment */}
        {view.comment && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Comment
            </h2>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {view.comment}
            </p>
          </div>
        )}

        {/* View Definition */}
        {view.definition && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              View Definition
            </h2>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
                {view.definition}
              </pre>
            </div>
          </div>
        )}

        {/* Columns */}
        {view.columns && view.columns.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Columns ({view.columns.length})
            </h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Column
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nullable
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Default
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {view.columns.map((column, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {column.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {column.type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          column.nullable
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {column.nullable ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {column.defaultValue || 'None'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Information */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
            About Database Views
          </h3>
          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
            <p>• Views are virtual tables based on SQL queries</p>
            <p>• They don't store data but show results from underlying tables</p>
            <p>• Updatable views can accept INSERT, UPDATE, and DELETE operations</p>
            <p>• Views can simplify complex queries and provide data security</p>
            <p>• Materialized views (PostgreSQL) store query results for better performance</p>
          </div>
        </div>
      </div>
      
      {/* Edit View Panel */}
      {isEditPanelOpen && (
        <EditViewPanel
          connection={connection}
          database={database}
          viewName={viewName}
          schema={schema}
          initialView={view}
          onClose={() => {
            setIsEditPanelOpen(false)
            onEditPanelClose?.()
          }}
          onSuccess={handleEditSuccess}
        />
      )}
      
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false) }}
        onConfirm={() => { void handleDeleteConfirm() }}
        title="Drop View"
        message={`Are you sure you want to drop the view "${viewName}"? This action cannot be undone.`}
        confirmText="Drop View"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}
